import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OverflowTooltip } from "src/domains/shared/components/OverflowTooltip";
import { Role, UserOfProject } from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.modal";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import {
  CAPTION,
  HOVER_TRANSITION_MS,
  LineGlyph,
  REDUCED_MOTION_SX,
  SUBTLE_SURFACE,
} from "src/domains/shared/components/StateTransition";
import {
  MYPAGE_AVATAR_TONES,
  MYPAGE_PENDING_CHIP_BG,
  MYPAGE_PENDING_CHIP_TEXT,
  MYPAGE_SECONDARY_ICON,
} from "../myPageStyles";
import { RoleToggle } from "./RoleToggle";

interface MemberCardProps {
  member: UserOfProject;
  role: Role;
  modified: boolean;
  onChangeRole: (role: Role) => void;
  onRequestDelete: () => void;
}

const CARD_HEIGHT_PX = 56;

/** 멤버 카드 한 장 — 텍스트 길이와 무관하게 높이·토글 위치가 고정된다. */
export const MemberCard: FC<MemberCardProps> = ({ member, role, modified, onChangeRole, onRequestDelete }) => {
  const { t } = useTranslation("projects");
  const displayName = member.name ?? member.username ?? member.email;
  const meta = [member.email, member.company, member.job].filter(Boolean).join(" · ");
  const tone = MYPAGE_AVATAR_TONES[Math.abs(member.id) % MYPAGE_AVATAR_TONES.length];

  return (
    <Box
      sx={{
        height: CARD_HEIGHT_PX,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "0 8px 0 10px",
        borderRadius: "8px",
        border: `1px solid ${modified ? COLOR_CONSTANTS.primary.main : "rgba(0, 0, 0, 0.12)"}`,
        backgroundColor: modified ? COLOR_CONSTANTS.primary._states.selected : COLOR_CONSTANTS.common.white_states.main,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        transition: `border-color ${HOVER_TRANSITION_MS}ms ease-out, background-color ${HOVER_TRANSITION_MS}ms ease-out`,
        "&:hover": modified ? undefined : { backgroundColor: SUBTLE_SURFACE },
        ...REDUCED_MOTION_SX,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 32,
          height: 32,
          borderRadius: "6px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: tone,
          color: COLOR_CONSTANTS.common.white_states.main,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {displayName.charAt(0).toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <OverflowTooltip text={displayName} placement="bottom-start" sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }} />
          {member.status !== "joined" && (
            <Typography
              component="span"
              sx={{
                flexShrink: 0,
                height: 18,
                padding: "0 6px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: MYPAGE_PENDING_CHIP_BG,
                color: MYPAGE_PENDING_CHIP_TEXT,
              }}
            >
              {t("myPage.permission.status.notJoined", { ns: "projects" })}
            </Typography>
          )}
        </Box>
        <OverflowTooltip text={meta} placement="bottom-start" sx={{ fontSize: 11, lineHeight: 1.3, color: CAPTION }} />
      </Box>
      <RoleToggle
        ariaLabel={t("myPageModal.permission.roleGroup", { ns: "projects", name: displayName })}
        value={role}
        onChange={onChangeRole}
      />
      <Box
        component="button"
        type="button"
        aria-label={t("myPageModal.permission.deleteMember", { ns: "projects", name: displayName })}
        onClick={onRequestDelete}
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          padding: 0,
          border: 0,
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          color: MYPAGE_SECONDARY_ICON,
          cursor: "pointer",
          transition: "background-color 120ms ease, color 120ms ease",
          "&:hover": { color: COLOR_CONSTANTS.error.main, backgroundColor: COLOR_CONSTANTS.error._states.hover },
          "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${COLOR_CONSTANTS.primary._states.focusVisible}` },
          ...REDUCED_MOTION_SX,
        }}
      >
        <LineGlyph name="trash" />
      </Box>
    </Box>
  );
};
